import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Webhook as WebhookIcon, Trash2, Play, CheckCircle2, XCircle, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Webhooks() {
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [logsFor, setLogsFor] = useState(null);
  const [logs, setLogs] = useState([]);

  const load = useCallback(async () => {
    const [{ data }, { data: e }] = await Promise.all([api.get("/webhooks"), api.get("/webhooks/events")]);
    setItems(data);
    setEvents(e);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id) => {
    if (!confirm("Excluir webhook?")) return;
    await api.delete(`/webhooks/${id}`);
    toast.success("Removido");
    load();
  };

  const sendTest = async (id) => {
    await api.post(`/webhooks/${id}/test`);
    toast.success("Evento de teste disparado");
    if (logsFor === id) loadLogs(id);
  };

  const loadLogs = async (id) => {
    const { data } = await api.get(`/webhooks/${id}/logs`);
    setLogs(data);
    setLogsFor(id);
  };

  return (
    <div className="space-y-4">
      <div className="card-soft px-5 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-[var(--text-2)] font-medium">Integrações</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Webhooks</h1>
          <p className="text-xs text-[var(--text-2)] mt-1">
            Receba eventos em tempo real (HMAC SHA-256). Cabeçalhos: <span className="font-mono">X-Nafe-Event</span>, <span className="font-mono">X-Nafe-Signature</span>.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-2xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-10 text-xs font-semibold px-4" data-testid="new-webhook-btn">
          <Plus size={14} className="mr-1" /> Novo webhook
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.length === 0 && (
          <div className="card-soft p-10 text-center text-[var(--text-2)] col-span-full">Nenhum webhook</div>
        )}
        {items.map((w) => (
          <div key={w.id} className="card-soft p-5" data-testid={`webhook-${w.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-2xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center shrink-0">
                  <WebhookIcon size={20} />
                </div>
                <div className="min-w-0">
                  <div className="font-display text-base font-bold tracking-tight truncate">{w.name}</div>
                  <a href={w.url} target="_blank" rel="noreferrer" className="text-xs font-mono text-[var(--text-2)] hover:text-[var(--brand)] hover:underline break-all">
                    {w.url}
                  </a>
                </div>
              </div>
              <span className={`pill ${w.is_active ? "pill-success" : "pill-neutral"}`}>{w.is_active ? "Ativo" : "Inativo"}</span>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {w.events.map((e) => <span key={e} className="pill pill-info">{e}</span>)}
            </div>

            <div className="mt-3 flex items-center gap-2 text-xs">
              <span className="text-[var(--text-2)] font-medium">Secret</span>
              <code className="font-mono bg-[var(--surface-2)] px-2 py-1 rounded-md text-[var(--text-1)]">{w.secret?.slice(0, 8)}…</code>
              <button onClick={() => { navigator.clipboard.writeText(w.secret); toast.success("Copiado"); }} className="text-[var(--text-2)] hover:text-[var(--brand)]" data-testid={`copy-secret-${w.id}`}>
                <Copy size={12} />
              </button>
            </div>

            <div className="flex gap-2 mt-4 border-t border-[var(--border-c)] pt-3">
              <button onClick={() => sendTest(w.id)} className="inline-flex items-center gap-1 px-3 h-9 rounded-xl bg-[var(--surface-2)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)] text-xs font-semibold" data-testid={`test-${w.id}`}>
                <Play size={12} /> Testar
              </button>
              <button onClick={() => loadLogs(w.id)} className="inline-flex items-center gap-1 px-3 h-9 rounded-xl bg-[var(--surface-2)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)] text-xs font-semibold" data-testid={`logs-${w.id}`}>
                Logs
              </button>
              <button onClick={() => remove(w.id)} className="ml-auto inline-flex items-center gap-1 px-3 h-9 rounded-xl bg-[var(--surface-2)] hover:bg-[var(--danger-soft)] hover:text-[var(--danger)] text-xs font-semibold text-[var(--text-2)]" data-testid={`del-${w.id}`}>
                <Trash2 size={12} />
              </button>
            </div>

            {logsFor === w.id && (
              <div className="mt-3 border-t border-[var(--border-c)] pt-3 max-h-60 overflow-y-auto space-y-1.5">
                <div className="text-xs text-[var(--text-2)] font-semibold">Últimas entregas</div>
                {logs.length === 0 && <div className="text-xs text-[var(--text-3)]">Sem registros</div>}
                {logs.map((l) => (
                  <div key={l.id} className="text-xs font-mono flex items-center gap-2 bg-[var(--surface-2)] rounded-lg px-2 py-1.5">
                    {l.ok ? <CheckCircle2 size={12} className="text-[var(--success)]" /> : <XCircle size={12} className="text-[var(--danger)]" />}
                    <span className="text-[var(--text-1)]">{l.event}</span>
                    <span className="text-[var(--text-2)]">{l.status_code || "ERR"}</span>
                    <span className="ml-auto text-[var(--text-3)]">{fmtDate(l.sent_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <CreateWebhook open={open} onOpenChange={setOpen} events={events} onCreated={load} />
    </div>
  );
}

function CreateWebhook({ open, onOpenChange, events, onCreated }) {
  const [form, setForm] = useState({ name: "", url: "", secret: "", events: [] });
  const toggle = (e) => setForm((f) => ({ ...f, events: f.events.includes(e) ? f.events.filter((x) => x !== e) : [...f.events, e] }));
  const submit = async (ev) => {
    ev.preventDefault();
    if (!form.events.length) { toast.error("Selecione ao menos 1 evento"); return; }
    try {
      await api.post("/webhooks", form);
      toast.success("Webhook criado");
      setForm({ name: "", url: "", secret: "", events: [] });
      onOpenChange(false);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="webhook-dialog">
        <DialogHeader><DialogTitle className="font-display font-bold">Novo webhook</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {[["name", "Nome"], ["url", "URL"], ["secret", "Secret (opcional, gerado se vazio)"]].map(([k, l]) => (
            <div key={k}>
              <label className="text-xs font-medium text-[var(--text-2)]">{l}</label>
              <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={k !== "secret"} className="rounded-xl h-10 mt-1" data-testid={`wh-input-${k}`} />
            </div>
          ))}
          <div>
            <label className="text-xs font-medium text-[var(--text-2)]">Eventos</label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {events.map((e) => (
                <button
                  type="button"
                  key={e}
                  onClick={() => toggle(e)}
                  className={`px-3 h-8 rounded-full text-xs font-mono transition-colors ${
                    form.events.includes(e)
                      ? "bg-[var(--brand)] text-white"
                      : "bg-[var(--surface-2)] text-[var(--text-2)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]"
                  }`}
                  data-testid={`evt-${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
          <Button type="submit" className="w-full rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-11" data-testid="wh-submit">Criar</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
